// services/publicBookingApi.js
// Public Booking API - No authentication required

import axios from 'axios';

// Follow existing pattern from secureApi.js
const API_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1/public`
  : 'http://localhost:4000/api/v1/public';

/**
 * Public Booking API Service
 * All endpoints are public (no authentication required)
 */
const publicBookingApi = {
  /**
   * Get property details by ID
   * @param {string} propertyId - Property MongoDB ID
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Property details
   */
  getProperty: async (propertyId, lang = 'ar') => {
    try {
      const response = await axios.get(`${API_URL}/properties/${propertyId}`, {
        params: { lang }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching property:', error);
      throw error;
    }
  },

  /**
   * Check availability and get pricing
   * @param {string} propertyId - Property MongoDB ID
   * @param {Object} data - { checkIn, checkOut, guests }
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Availability and pricing info
   */
  checkAvailability: async (propertyId, data, lang = 'ar') => {
    try {
      const response = await axios.post(
        `${API_URL}/properties/${propertyId}/check-availability`,
        data,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      throw error;
    }
  },

  /**
   * Get 60-day availability calendar
   * @param {string} propertyId - Property MongoDB ID
   * @param {string} lang - Language ('ar' or 'en')
   * @param {number} days - Number of days (default 60)
   * @returns {Promise} Calendar array with availability
   */
  getAvailabilityCalendar: async (propertyId, lang = 'ar', days = 60) => {
    try {
      const response = await axios.get(
        `${API_URL}/properties/${propertyId}/availability-calendar`,
        { params: { lang, days } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching availability calendar:', error);
      throw error;
    }
  },

  /**
   * Create a new booking
   * @param {Object} bookingData - Complete booking data
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Booking confirmation
   */
  createBooking: async (bookingData, lang = 'ar') => {
    try {
      const response = await axios.post(
        `${API_URL}/bookings/create`,
        bookingData,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  },

  /**
   * Lookup booking by confirmation code
   * @param {string} confirmationCode - Booking confirmation code
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Booking details
   */
  getBooking: async (confirmationCode, lang = 'ar') => {
    try {
      const response = await axios.get(
        `${API_URL}/bookings/${confirmationCode}`,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  },

  /**
   * Cancel booking
   * @param {string} confirmationCode - Booking confirmation code
   * @param {Object} data - { reason, guestEmail }
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Cancellation result
   */
  cancelBooking: async (confirmationCode, data, lang = 'ar') => {
    try {
      const response = await axios.post(
        `${API_URL}/bookings/${confirmationCode}/cancel`,
        data,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  },

  /**
   * Modify booking
   * @param {string} confirmationCode - Booking confirmation code
   * @param {Object} data - { guestEmail, checkIn, checkOut, numberOfGuests }
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Modification result with pricing
   */
  modifyBooking: async (confirmationCode, data, lang = 'ar') => {
    try {
      const response = await axios.patch(
        `${API_URL}/bookings/${confirmationCode}/modify`,
        data,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error modifying booking:', error);
      throw error;
    }
  },

  /**
   * Get property reviews
   * @param {string} propertyId - Property MongoDB ID
   * @param {Object} options - { page, limit, lang }
   * @returns {Promise} Reviews list
   */
  getPropertyReviews: async (propertyId, options = {}) => {
    try {
      const { page = 1, limit = 5, lang = 'ar' } = options;
      const response = await axios.get(
        `${API_URL}/properties/${propertyId}/reviews`,
        { params: { page, limit, lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching property reviews:', error);
      return { success: false, reviews: [], pagination: {} };
    }
  },

  /**
   * Get property review statistics
   * @param {string} propertyId - Property MongoDB ID
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Review statistics
   */
  getPropertyReviewStats: async (propertyId, lang = 'ar') => {
    try {
      const response = await axios.get(
        `${API_URL}/properties/${propertyId}/reviews/stats`,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching review stats:', error);
      return { success: false, stats: { averageRating: 0, totalReviews: 0 } };
    }
  },

  /**
   * Check if booking can be reviewed
   * @param {string} confirmationCode - Booking confirmation code
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Eligibility result { canReview, reason }
   */
  checkReviewEligibility: async (confirmationCode, lang = 'ar') => {
    try {
      const response = await axios.get(
        `${API_URL}/bookings/${confirmationCode}/review-eligibility`,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      return { success: false, canReview: false, reason: 'حدث خطأ في التحقق' };
    }
  },

  /**
   * Submit review for booking
   * @param {string} confirmationCode - Booking confirmation code
   * @param {Object} reviewData - { rating, comment, ratings, guestEmail, language }
   * @param {string} lang - Language ('ar' or 'en')
   * @returns {Promise} Review submission result
   */
  submitReview: async (confirmationCode, reviewData, lang = 'ar') => {
    try {
      const response = await axios.post(
        `${API_URL}/bookings/${confirmationCode}/review`,
        reviewData,
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }
};

export default publicBookingApi;
